'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface IoTIntegrationProps {
  currentLanguage: string;
}

const IoTIntegration = ({ currentLanguage }: IoTIntegrationProps) => {
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);

  const devices = [
    {
      id: 'smart-meters',
      name: 'Smart Energy Meters',
      icon: 'BoltIcon',
      status: 'active',
      count: 247,
      lastUpdate: '2 min ago',
      dataQuality: 99,
      metrics: ['Real-time consumption', 'Peak demand tracking', 'Power factor monitoring', 'Usage patterns'],
    },
    {
      id: 'sensors',
      name: 'Environmental Sensors',
      icon: 'CloudIcon',
      status: 'active',
      count: 156,
      lastUpdate: '5 min ago',
      dataQuality: 97,
      metrics: ['Temperature', 'Humidity', 'Air quality', 'Pressure'],
    },
    {
      id: 'fuel-monitors',
      name: 'Fuel Monitoring Systems',
      icon: 'FireIcon',
      status: 'active',
      count: 89,
      lastUpdate: '1 min ago',
      dataQuality: 98,
      metrics: ['Tank levels', 'Flow rates', 'Consumption alerts', 'Efficiency tracking'],
    },
    {
      id: 'vehicle-trackers',
      name: 'Vehicle Telematics',
      icon: 'TruckIcon',
      status: 'warning',
      count: 124,
      lastUpdate: '15 min ago',
      dataQuality: 92,
      metrics: ['GPS location', 'Fuel consumption', 'Idle time', 'Route optimization'],
    },
    {
      id: 'water-meters',
      name: 'Water Flow Meters',
      icon: 'BeakerIcon',
      status: 'active',
      count: 78,
      lastUpdate: '3 min ago',
      dataQuality: 96,
      metrics: ['Usage volume', 'Flow rates', 'Leak detection', 'Conservation metrics'],
    },
    {
      id: 'industrial',
      name: 'Industrial Equipment',
      icon: 'CogIcon',
      status: 'active',
      count: 203,
      lastUpdate: '4 min ago',
      dataQuality: 94,
      metrics: ['Operating hours', 'Energy consumption', 'Maintenance alerts', 'Efficiency data'],
    },
  ];

  const integrationBenefits = [
    {
      title: 'Automated Data Collection',
      description: 'Eliminate manual entry errors and ensure continuous monitoring',
      icon: 'ArrowPathIcon',
      impact: 'high',
    },
    {
      title: 'Real-Time Alerts',
      description: 'Instant notifications for anomalies and threshold breaches',
      icon: 'BellAlertIcon',
      impact: 'high',
    },
    {
      title: 'Predictive Maintenance',
      description: 'AI-driven insights to prevent equipment failures',
      icon: 'WrenchScrewdriverIcon',
      impact: 'medium',
    },
    {
      title: 'Historical Analysis',
      description: 'Comprehensive data storage for trend identification',
      icon: 'ChartBarIcon',
      impact: 'medium',
    },
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-success/10 px-4 py-2 rounded-full mb-4">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            <span className="text-sm font-semibold text-success">Live IoT Network</span>
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            IoT Integration Panel
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Connected device status, data quality metrics, and automated monitoring systems
          </p>
        </div>

        {/* Device Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { label: 'Total Devices', value: '897', icon: 'CpuChipIcon', color: 'primary' },
            { label: 'Active Now', value: '889', icon: 'SignalIcon', color: 'success' },
            { label: 'Avg Data Quality', value: '96%', icon: 'CheckCircleIcon', color: 'info' },
            { label: 'Uptime', value: '99.8%', icon: 'ClockIcon', color: 'warning' },
          ].map((stat, index) => (
            <div key={index} className="bg-card border border-border rounded-xl p-6 text-center">
              <Icon name={stat.icon} size={28} className={`text-${stat.color} mx-auto mb-3`} />
              <div className={`text-3xl font-heading font-bold text-${stat.color} mb-1`}>{stat.value}</div>
              <div className="text-sm text-text-secondary">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Device Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {devices.map((device) => (
            <div
              key={device.id}
              onClick={() => setSelectedDevice(device.id)}
              className={`bg-card border-2 rounded-xl p-6 cursor-pointer transition-all duration-300 ${
                selectedDevice === device.id
                  ? 'border-primary shadow-natural-lg'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-3 bg-${device.status === 'active' ? 'success' : 'warning'}/10 rounded-lg`}>
                    <Icon name={device.icon} size={28} className={`text-${device.status === 'active' ? 'success' : 'warning'}`} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-text-primary">{device.name}</h3>
                    <p className="text-xs text-text-secondary">{device.count} devices</p>
                  </div>
                </div>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    device.status === 'active' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                  }`}
                >
                  {device.status}
                </span>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-text-secondary">Data Quality</span>
                  <span className="font-semibold text-text-primary">{device.dataQuality}%</span>
                </div>
                <div className="w-full bg-border rounded-full h-2 overflow-hidden">
                  <div
                    className={`bg-${device.dataQuality > 95 ? 'success' : 'warning'} h-full rounded-full transition-all duration-500`}
                    style={{ width: `${device.dataQuality}%` }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                {device.metrics.map((metric, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <Icon name="CircleStackIcon" size={14} className="text-primary" />
                    <span className="text-xs text-text-primary">{metric}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-border text-xs text-text-secondary">
                Last update: {device.lastUpdate}
              </div>
            </div>
          ))}
        </div>

        {/* Integration Benefits */}
        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/20 rounded-2xl p-8">
          <h3 className="font-heading font-bold text-2xl text-text-primary mb-8 text-center">
            IoT Integration Benefits
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {integrationBenefits.map((benefit, index) => (
              <div key={index} className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <div className={`p-3 bg-${benefit.impact === 'high' ? 'primary' : 'info'}/10 rounded-lg flex-shrink-0`}>
                    <Icon name={benefit.icon} size={28} className={`text-${benefit.impact === 'high' ? 'primary' : 'info'}`} />
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-text-primary">{benefit.title}</h4>
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                          benefit.impact === 'high' ?'bg-primary/10 text-primary' :'bg-info/10 text-info'
                        }`}
                      >
                        {benefit.impact === 'high' ? 'High Impact' : 'Medium Impact'}
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IoTIntegration;